import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { Button } from "./Button";
import { faBell, faChartLine, faCheck, faGlobe, faHandsHelping, faRobot, faSearch, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Auth, API, graphqlOperation } from "aws-amplify";
import { API_DEFAULT_HEADERS, API_DOMAIN } from "../APIconfig";
import { Spinner } from "./Spinner";

interface ViewLimitPopupProps {
  onClose: () => void;
}

export interface PremiumFeature {
  icon: any;
  text: string;
}

export interface PricingPlan {
  name: string;
  description: string;
  monthlyPrice: string;
  yearlyPrice: string;
  yearlySavings: string;
  features: string[];
  highlighted?: boolean;
  monthlyPriceId: string;
  yearlyPriceId: string;
}

export const premiumFeatures: PremiumFeature[] = [
  { icon: faSearch, text: "Voor u geselecteerde aanbestedingen" },
  { icon: faGlobe, text: "Internationale aanbestedingen uit 5 landen" },
  { icon: faChartLine, text: "Uitgebreide concurrentie analyse" },
  { icon: faHandsHelping, text: "Hulp bij inschrijven" },
  { icon: faRobot, text: "Door AI gestuurde CPV-codes" },
  { icon: faBell, text: "Gepersonaliseerde e-mailmeldingen" },
];

export const pricingPlans: PricingPlan[] = [
  {
    name: 'Light',
    description: 'Voor toegang tot alle Nederlandse aanbestedingen',
    monthlyPrice: '8,95',
    yearlyPrice: '89,50',
    yearlySavings: '17,90',
    features: [
      'Onbeperkt aanbestedingen bekijken',
      'Beperkte concurrentie analyse',
    ],
    monthlyPriceId: process.env.REACT_APP_STRIPE_LIGHT_MONTHLY_PRICE_ID || 'price_1RTghDBsOwaHH4Y2lX1i3R8c',
    yearlyPriceId: process.env.REACT_APP_STRIPE_LIGHT_YEARLY_PRICE_ID || 'price_1RTghQBsOwaHH4Y2DkyOsJUf'
  },
  {
    name: 'Premium',
    description: 'Voor toegang tot alle aanbestedingen uit 5 landen',
    monthlyPrice: '12,95',
    yearlyPrice: '129,50',
    yearlySavings: '25,90',
    features: [
      'Alles van Light, plus:',
      'CPV codes instellen met een expert',
      'Internationale aanbestedingen',
      'Volledige concurrentie analyse',
      'Hulp bij inschrijven'
    ],
    highlighted: true,
    monthlyPriceId: process.env.REACT_APP_STRIPE_PREMIUM_MONTHLY_PRICE_ID || 'price_1RE7EUBsOwaHH4Y2NNo1ojzi',
    yearlyPriceId: process.env.REACT_APP_STRIPE_PREMIUM_YEARLY_PRICE_ID || 'price_1RE7FlBsOwaHH4Y2WAfzC5M8'
  }
];

const getUserQuery = `
  query GetUser($id: ID!) {
    getUser(id: $id) {
      stripe_id
    }
  }
`;

const ViewLimitPopup: React.FC<ViewLimitPopupProps> = ({ onClose }) => {
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubscribe = async (plan: PricingPlan) => {
    console.log("handleSubscribe called with plan:", plan);
    console.log("Selected billing period:", selectedPlan);
    setLoading(true);
    try {
      const user = await Auth.currentAuthenticatedUser();
      const userId = user.attributes.sub; 
      console.log("Authenticated user ID (sub):", userId);

      const gqlResponse: any = await API.graphql(graphqlOperation(getUserQuery, { id: userId }));
      const stripeId = gqlResponse.data?.getUser?.stripe_id;
      console.log("Stripe Customer ID from GraphQL:", stripeId);

      if (!stripeId) {
        console.error("Stripe Customer ID not found for user via GraphQL. Halting subscription.");
        alert("Er is een probleem met uw account configuratie (GraphQL). Neem contact op met support.");
        setLoading(false);
        return;
      }

      const priceID = selectedPlan === 'monthly' ? plan.monthlyPriceId : plan.yearlyPriceId;
      console.log("Selected Price ID for Stripe:", priceID);

      const session = await Auth.currentSession();
      const jwt = session.getIdToken().getJwtToken();
      console.log("Retrieved JWT.");

      const raw = JSON.stringify({
        customerID: stripeId,
        priceID: priceID,
        locale: "nl",
      });

      const requestOptions: RequestInit = {
        method: "POST",
        headers: new Headers(API_DEFAULT_HEADERS(jwt)),
        body: raw,
      };

      console.log("Sending request to /stripe/pay with options:", requestOptions);
      const response = await fetch(`${API_DOMAIN}/stripe/pay`, requestOptions);
      const jsonResponse = await response.json();
      console.log("Response from /stripe/pay:", response.status, jsonResponse);

      if (response.ok && jsonResponse.body) {
        console.log("Redirecting to Stripe checkout:", jsonResponse.body); 
        window.location.replace(jsonResponse.body); 
      } else {
        console.error("Stripe payment initiation failed:", jsonResponse);
        alert("Betaling kon niet worden gestart. Probeer het later opnieuw of neem contact op met support."); 
        setLoading(false);
      }
    } catch (error) {
      console.error("Error during subscription process:", error);
      alert("Er is een onverwachte fout opgetreden. Probeer het later opnieuw of neem contact op met support."); 
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[rgba(40,79,130,0.2)]"
      style={{ color: 'white' }}
    >
      <div
        className="bg-white w-full max-w-2xl md:max-w-4xl mx-2 rounded-xl shadow-2xl overflow-auto max-h-[90vh] flex flex-col relative"
        style={{ textAlign: 'center' }}
      >
        {loading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center z-20 rounded-xl">
            <Spinner />
            <p className="pt-3 text-white">Betaling verwerken...</p>
          </div>
        )}
        {/* X Close Icon */}
        <button
          onClick={onClose}
          aria-label="Popup sluiten"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 focus:outline-none text-2xl z-10"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <div className="p-4 md:p-8 flex-1 overflow-auto">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Weergavelimiet bereik
            </h2>
            <p className="text-gray-600 max-w-lg mx-auto mb-6">
              Je hebt je limiet van 5 gratis weergaven bereikt. Deze limiet geldt na een introductieperiode van 3 dagen onbeperkt toegang. Kies een abonnement voor blijvend onbeperkte toegang en extra functies.
            </p>
            <div className="mt-8 flex justify-center">
              <div className="relative bg-gray-100 p-1 rounded-lg inline-flex">
                <button
                  onClick={() => setSelectedPlan('monthly')}
                  className={`${selectedPlan === 'monthly'
                      ? 'bg-white shadow-sm'
                      : 'hover:bg-gray-50'
                  } relative px-4 py-2 rounded-md text-sm font-medium text-blue-600 transition-all duration-200`}
                >
                  Maandelijks
                </button>
                <button
                  onClick={() => setSelectedPlan('yearly')}
                  className={`${selectedPlan === 'yearly'
                      ? 'bg-white shadow-sm'
                      : 'hover:bg-gray-50'
                  } relative px-4 py-2 rounded-md text-sm font-medium text-blue-600 transition-all duration-200`}
                >
                  Jaarlijks
                  <span className="absolute -top-2 -right-2 bg-primary-pink text-white text-xs px-2 py-0.5 rounded-full">
                    Bespaar
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Pricing plans grid - always rendered */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {pricingPlans.map((plan) => (
              <div
                key={plan.name}
                className={`relative rounded-2xl ${
                  plan.highlighted
                    ? 'border-2 border-primary-pink shadow-xl'
                    : 'border border-gray-200'
                } p-6 md:p-8 bg-white flex flex-col min-h-[400px]`}
              >
                {plan.highlighted && (
                  <span className="absolute top-0 right-6 -translate-y-1/2 bg-primary-pink text-white px-3 py-0.5 rounded-full text-sm font-semibold">
                    Aanbevolen
                  </span>
                )}
                <div className="pb-5">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                    <p className="mt-2 text-gray-500">{plan.description}</p>
                  </div>
                  <div className="mb-8">
                    <p className="text-4xl font-bold text-gray-900">
                      €{selectedPlan === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice}
                      <span className="text-lg font-normal text-gray-500">
                        /{selectedPlan === 'monthly' ? 'maand' : 'jaar'}
                      </span>
                    </p>
                    {selectedPlan === 'yearly' && (
                      <p className="mt-1 text-sm text-primary-pink">
                        Bespaar {plan.yearlySavings} per jaar
                      </p>
                    )}
                  </div>
                  <ul className="space-y-4 flex-grow">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start">
                        <FontAwesomeIcon 
                          icon={faCheck} 
                          className={`mt-1 mr-3 ${plan.highlighted ? 'text-primary-pink' : 'text-blue-600'}`}
                        />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto">
                  <Button
                    type={plan.highlighted ? "pink" : "primary"}
                    className="w-full justify-center"
                    onClick={() => handleSubscribe(plan)}
                  >
                    Start met {plan.name}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewLimitPopup;
